/**
 * GA4 analytics integration — consent-aware.
 *
 * Default: analytics_storage denied. Only loads GA4 script and
 * grants storage after CookieConsent "Accept" is clicked.
 *
 * SPA page-view tracking via route change listener.
 */

const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

/**
 * Initialize GA4 with default-denied consent mode.
 * Called once in main.tsx before the app mounts.
 */
export function initAnalytics() {
  if (!GA4_ID) return;

  // Set up dataLayer and default consent before any gtag calls
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).dataLayer = (window as any).dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function gtag(...args: any[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer.push(args);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).gtag = gtag;

  // Consent Mode v2: default to denied
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  // If user previously consented, upgrade immediately
  const consent = localStorage.getItem("hideaway-cookie-consent");
  if (consent === "accepted") {
    gtag("consent", "update", {
      analytics_storage: "granted",
    });

    // Load GA4 script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(script);

    gtag("js", new Date());
    gtag("config", GA4_ID, { send_page_view: true });
  }
}

/**
 * Track a page view. Call on every route change.
 */
export function trackPageView(path: string, title?: string) {
  if (!GA4_ID || typeof window.gtag !== "function") return;
  window.gtag("config", GA4_ID, {
    page_path: path,
    page_title: title,
  });
}

// Extend Window for gtag
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}
